import React from 'react';
import { Home, Building, Factory, Store, Compass, ArrowRight, CheckCircle } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

const VASTU_BG = '/media/stock/b4d3fea4_pexels-photo-7222052.jpeg?auto=compress&cs=tinysrgb&w=1200';

const vastuServices = [
  {
    icon: Home,
    title: 'Residential Vastu', titleHi: 'आवासीय वास्तु',
    description: 'Complete home Vastu analysis for peace, prosperity and positive energy',
    descHi: 'शांति, समृद्धि और सकारात्मक ऊर्जा के लिए संपूर्ण गृह वास्तु विश्लेषण',
    features: ['Main entrance direction', 'Room placement', 'Kitchen & bedroom tips', 'Remedies without demolition'],
    featuresHi: ['मुख्य द्वार की दिशा', 'कमरों की स्थिति', 'रसोई और शयनकक्ष सुझाव', 'बिना तोड़फोड़ उपाय']
  },
  {
    icon: Building,
    title: 'Office Vastu', titleHi: 'ऑफिस वास्तु',
    description: 'Boost business growth and employee productivity with office Vastu',
    descHi: 'ऑफिस वास्तु से व्यापार वृद्धि और कर्मचारी उत्पादकता बढ़ाएं',
    features: ['Desk & cabin placement', 'Cash counter position', 'Meeting room setup', 'Success & growth tips'],
    featuresHi: ['डेस्क एवं केबिन स्थिति', 'कैश काउंटर की जगह', 'मीटिंग रूम व्यवस्था', 'सफलता के उपाय']
  },
  {
    icon: Factory,
    title: 'Industrial Vastu', titleHi: 'औद्योगिक वास्तु',
    description: 'Factory and industrial unit Vastu for smooth operations',
    descHi: 'फैक्ट्री और औद्योगिक इकाई वास्तु — सुचारू संचालन के लिए',
    features: ['Machine placement', 'Raw material storage', 'Worker welfare', 'Production enhancement'],
    featuresHi: ['मशीन की स्थिति', 'कच्चे माल का भंडारण', 'कर्मचारी कल्याण', 'उत्पादन वृद्धि']
  },
  {
    icon: Store,
    title: 'Shop/Showroom Vastu', titleHi: 'दुकान वास्तु',
    description: 'Attract more customers and increase sales with shop Vastu',
    descHi: 'दुकान वास्तु से अधिक ग्राहक आकर्षित करें और बिक्री बढ़ाएं',
    features: ['Display arrangement', 'Cash counter position', 'Customer flow', 'Signage placement'],
    featuresHi: ['प्रदर्शन व्यवस्था', 'कैश काउंटर की जगह', 'ग्राहक प्रवाह', 'साइनेज स्थान']
  }
];

export default function Vastu() {
  const { lang, t } = useLang();
  return (
    <section id="vastu" className="section-padding relative overflow-hidden" data-testid="vastu-section">
      {/* Background */}
      <div className="absolute inset-0 cosmic-bg-alt" />
      <div className="absolute top-0 left-1/3 w-96 h-96 bg-green-900/10 rounded-full blur-3xl" />
      
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs tracking-[0.3em] uppercase text-gold font-semibold font-body">
            {t('vastu.tag')}
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-text-main mt-4 mb-6 tracking-tight">
            {t('vastu.title1')} <span className="text-gold-gradient">{t('vastu.title2')}</span>
          </h2>
          <p className="text-text-muted leading-relaxed">
            {lang === 'hi'
              ? 'वास्तु शास्त्र प्रकृति और रहने की जगह में संतुलन लाता है, सकारात्मक ऊर्जा और समृद्धि को आकर्षित करता है।'
              : 'Vastu Shastra brings balance between nature and living spaces, attracting positive energy and prosperity.'}
          </p>
        </div>

        {/* Vastu Services Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {vastuServices.map((service, index) => (
            <div 
              key={index} 
              className="glass-card p-8 group"
              data-testid={`vastu-card-${index}`}
            >
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/15 to-green-600/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform border border-green-500/10">
                  <service.icon className="w-8 h-8 text-gold" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-2xl text-text-main mb-1">{lang === 'hi' ? service.titleHi : service.title}</h3>
                  <p className="text-primary text-sm mb-3">{lang === 'hi' ? service.title : service.titleHi}</p>
                  <p className="text-text-muted text-sm mb-4">{lang === 'hi' ? service.descHi : service.description}</p>
                  <ul className="space-y-2">
                    {(lang === 'hi' ? service.featuresHi : service.features).map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-2 text-sm text-text-muted">
                        <CheckCircle className="w-4 h-4 text-gold flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Vastu Principles */}
        <div className="glass-card p-8 md:p-12 relative overflow-hidden">
          {/* Background image overlay */}
          <div className="absolute inset-0 opacity-5">
            <img src={VASTU_BG} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="relative z-10 grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Compass className="w-8 h-8 text-gold" />
                <h3 className="font-heading text-3xl text-text-main">{lang === 'hi' ? 'वास्तु के मूल सिद्धांत' : 'Key Vastu Principles'}</h3>
              </div>
              <p className="text-text-muted mb-8">
                {lang === 'hi' 
                  ? 'हमारा वास्तु परामर्श पांच तत्वों और आठ दिशाओं के प्राचीन सिद्धांतों का पालन करके आपके स्थान में सामंजस्य स्थापित करता है।'
                  : 'Our Vastu consultation follows the ancient principles of five elements and eight directions to create perfect harmony in your space.'}
              </p>
              <a 
                href="#contact" 
                className="btn-gold inline-flex items-center gap-2"
                data-testid="vastu-consultation-btn"
              >
                {t('vastu.cta')}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { direction: 'North', dirHi: 'उत्तर', element: lang === 'hi' ? 'जल' : 'Water', benefit: lang === 'hi' ? 'धन एवं करियर' : 'Wealth & Career', color: 'from-blue-500/20 to-blue-600/10' },
                { direction: 'East', dirHi: 'पूर्व', element: lang === 'hi' ? 'वायु' : 'Air', benefit: lang === 'hi' ? 'स्वास्थ्य एवं विकास' : 'Health & Growth', color: 'from-green-500/20 to-green-600/10' },
                { direction: 'South', dirHi: 'दक्षिण', element: lang === 'hi' ? 'अग्नि' : 'Fire', benefit: lang === 'hi' ? 'यश एवं प्रतिष्ठा' : 'Fame & Recognition', color: 'from-red-500/20 to-red-600/10' },
                { direction: 'West', dirHi: 'पश्चिम', element: lang === 'hi' ? 'आकाश' : 'Space', benefit: lang === 'hi' ? 'स्थिरता एवं सफलता' : 'Stability & Success', color: 'from-purple-500/20 to-purple-600/10' },
              ].map((item, index) => (
                <div key={index} className={`bg-gradient-to-br ${item.color} rounded-xl p-4 text-center border border-primary/10`}>
                  <p className="font-heading text-xl text-gold mb-1">{item.direction}</p>
                  <p className="text-xs text-text-muted/70 mb-2">{item.dirHi}</p>
                  <p className="text-sm text-text-main font-medium">{item.element}</p>
                  <p className="text-xs text-text-muted">{item.benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
